import React, { useContext, useState, useEffect } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Paper,
  Autocomplete,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import { GlobalContext } from "../../contexts/GlobalContext"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../api"
import Swal from "sweetalert2"
import dayjs from "dayjs"
import getIconComponent from "../utils/getIconComponent"

// Importaciones para Exportación CSV y PDF Personalizada
import { mkConfig, generateCsv, download } from "export-to-csv"
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"

// Íconos
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import PersonSearchIcon from "@mui/icons-material/PersonSearch"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const ConsEstadodeCuentaIndex = () => {
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [expanded, setExpanded] = useState(true)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  const [criterioBusqueda, setCriterioBusqueda] = useState("")
  const [nombreCliente, setNombreCliente] = useState("")

  const [filtros, setFiltros] = useState({
    ciacodigo: "",
    clicodigo: "",
    fecEmiDesde: null,
    fecEmiHasta: dayjs(),
    chkDG: "N",
  })

  const { data: listaCompanias = [], isLoading: isLoadingCompanias } = useQuery({
    queryKey: ["getCatalogosEstadoCuenta"],
    queryFn: async () => {
      try {
        const response = await api.post("/ConsEstadodeCuenta/getCatalogosEstadoCuenta", {})
        const findArray = (obj) => {
          if (!obj) return []
          if (Array.isArray(obj)) return obj
          if (Array.isArray(obj.companias)) return obj.companias
          if (obj.data) return findArray(obj.data)
          return []
        }
        return findArray(response)
      } catch (error) {
        return []
      }
    },
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (listaCompanias.length > 0 && !filtros.ciacodigo) {
      handleInputChange("ciacodigo", listaCompanias[0].id)
    }
  }, [listaCompanias, filtros.ciacodigo])

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleBuscarCliente = async () => {
    if (!filtros.ciacodigo) {
      Swal.fire("Aviso", "Seleccione una Compañía primero.", "warning")
      return
    }
    if (!criterioBusqueda.trim()) return

    try {
      const response = await api.post("/ConsEstadodeCuenta/getClienteBusqueda", {
        ciacodigo: filtros.ciacodigo,
        criterio: criterioBusqueda,
      })

      if (response.data.success) {
        handleInputChange("clicodigo", response.data.data.clicodigo)
        setCriterioBusqueda(response.data.data.clicodigo)
        setNombreCliente(response.data.data.clinombre)
      } else {
        Swal.fire("Aviso", response.data.message || "Cliente no encontrado.", "info")
        handleInputChange("clicodigo", "")
        setNombreCliente("")
      }
    } catch (error) {
      Swal.fire("Error", "Ocurrió un problema al buscar el cliente.", "error")
    }
  }

  const handleKeyDownBusqueda = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleBuscarCliente()
    }
  }

  const handleFiltrar = () => {
    if (!filtros.clicodigo.trim()) {
      Swal.fire({
        title: "Aviso",
        text: "Busque y seleccione un Cliente válido.",
        icon: "warning",
        confirmButtonColor: "#196C87",
      })
      return
    }
    if (!filtros.fecEmiDesde || !filtros.fecEmiHasta) {
      Swal.fire("Aviso", "El rango de fechas es obligatorio.", "warning")
      return
    }
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  const fetchAllDataForExport = async () => {
    try {
      Swal.fire({
        title: "Generando Archivo...",
        text: "Extrayendo el Estado de Cuenta...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })

      const payload = {
        page: 1,
        perPage: 999999,
        filters: {},
        externalFilters: {
          ...filtros,
          fecEmiDesde: filtros.fecEmiDesde ? dayjs(filtros.fecEmiDesde).format("DD-MM-YYYY") : "",
          fecEmiHasta: filtros.fecEmiHasta ? dayjs(filtros.fecEmiHasta).format("DD-MM-YYYY") : "",
        },
      }

      const response = await fetchwrapper("/ConsEstadodeCuenta/getEstadoCuentaCliente", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      const result = await response.json()
      const allRows = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.data?.data)
          ? result.data.data
          : []

      if (allRows.length === 0) {
        Swal.fire("Aviso", "No hay datos para exportar.", "info")
        return null
      }
      return allRows
    } catch (error) {
      Swal.fire("Error", "No se pudo generar la exportación.", "error")
      return null
    }
  }

  // --- EXPORTACIÓN CSV APLANADA ---
  const handleExportarEstadoCuentaCSV = async () => {
    const allData = await fetchAllDataForExport()
    if (!allData) return

    const dataAplanada = allData
      .map((row) => {
        if (row.es_separador) return null
        const jerarquia = row.es_resumen
          ? "1-RESUMEN INICIAL"
          : row.es_cabecera
            ? "2-DOCUMENTO PRINCIPAL"
            : "3-DETALLE / ABONO"
        const docLimpio = row.documento ? row.documento.replace("↪", "").trim() : ""

        return {
          Jerarquía: jerarquia,
          "Fecha de Emisión": row.fecha || "",
          "Fecha de Vencimiento": row.vencimiento || "",
          "Tipo de Transacción": row.tipo || "",
          "Documento / Detalle": docLimpio,
          "Observación / Consumo": row.observacion || "",
          "Referencia de Pago": row.referencia || "",
          "Valor (USD)": row.valor !== null ? Number(row.valor).toFixed(2) : "",
          "Saldo Acumulado (USD)": row.saldo !== null ? Number(row.saldo).toFixed(2) : "",
        }
      })
      .filter((row) => row !== null)

    const csvConfig = mkConfig({
      fieldSeparator: ",",
      decimalSeparator: ".",
      useKeysAsHeaders: true,
      filename: `Estado_Cuenta_Cliente_${filtros.clicodigo}_${dayjs().format("YYYYMMDD")}`,
    })

    const csv = generateCsv(csvConfig)(dataAplanada)
    download(csvConfig)(csv)
    Swal.close()
  }

  // --- EXPORTACIÓN PDF BANCARIA PERSONALIZADA ---
  const generarPDFBancario = async () => {
    const data = await fetchAllDataForExport()
    if (!data) return

    // Documento en formato Horizontal (Landscape) para que entren todas las columnas holgadamente
    const doc = new JsPDF("l", "pt", "a4")
    const comp = listaCompanias.find((c) => String(c.id) === String(filtros.ciacodigo))
    const nombreCia = comp ? comp.label : "Compañía"

    // --- ENCABEZADO INSTITUCIONAL ---
    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135) // #196C87
    doc.text(nombreCia, 40, 40)

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text("ESTADO DE CUENTA", 40, 65)

    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)
    doc.text(`Cliente: ${filtros.clicodigo} - ${nombreCliente}`, 40, 85)
    doc.text(
      `Período: ${filtros.fecEmiDesde ? dayjs(filtros.fecEmiDesde).format("DD/MM/YYYY") : ""} al ${filtros.fecEmiHasta ? dayjs(filtros.fecEmiHasta).format("DD/MM/YYYY") : ""}`,
      40,
      100,
    )
    doc.text(`Fecha de Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 40, 115)

    const tableColumn = [
      "Emisión",
      "Vence",
      "Documento / Detalle",
      "Tipo",
      "Valor",
      "Saldo",
      "Referencia",
      "Observación",
    ]
    const tableRows = []

    data.forEach((row) => {
      let docText = row.documento || ""
      if (!row.es_cabecera && !row.es_separador && !row.es_resumen) {
        docText = "   " + docText // Sangría para el detalle
      }

      tableRows.push([
        row.fecha || "",
        row.vencimiento || "",
        docText,
        row.tipo || "",
        row.es_separador ? "" : row.valor !== null ? `$${Number(row.valor).toFixed(2)}` : "",
        row.es_separador ? "" : row.saldo !== null ? `$${Number(row.saldo).toFixed(2)}` : "",
        row.referencia || "",
        row.observacion || "",
      ])
    })

    autoTable(doc, {
      startY: 135,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      didParseCell: function (dataInfo) {
        const rowIndex = dataInfo.row.index
        const rowData = data[rowIndex]

        if (rowData) {
          // Colorización y estilos dinámicos idénticos a los de la pantalla
          if (rowData.es_separador) {
            dataInfo.cell.styles.fillColor = [25, 108, 135] // Banda azul sólido
            dataInfo.cell.styles.textColor = [255, 255, 255]
            dataInfo.cell.styles.fontStyle = "bold"
            dataInfo.cell.styles.halign = "center"
          } else if (rowData.es_resumen) {
            dataInfo.cell.styles.fillColor = [255, 253, 231] // Amarillo claro para resumen
          }

          if (rowData.es_cabecera) {
            dataInfo.cell.styles.fontStyle = "bold"
          }

          // Dar color rojo a los valores negativos y verde a los documentos en garantía
          if (dataInfo.column.index === 4 || dataInfo.column.index === 5) {
            const valStr = dataInfo.cell.raw || ""
            if (valStr.includes("$-")) {
              dataInfo.cell.styles.textColor = [211, 47, 47]
            } else if (rowData.tipo_cod === "DG" || (rowData.observacion && rowData.observacion.includes("Garantía"))) {
              dataInfo.cell.styles.textColor = [46, 125, 50]
            }
          }
        }
      },
    })

    doc.save(`Estado_Cuenta_${filtros.clicodigo}_${dayjs().format("YYYYMMDD")}.pdf`)
    Swal.close()
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Estado de Cuenta del Cliente</b>
        </div>

        <CustomBackdrop isLoading={isLoadingCompanias} />

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Criterios de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={listaCompanias}
                    getOptionLabel={(option) => option?.label || ""}
                    value={listaCompanias.find((c) => String(c.id) === String(filtros.ciacodigo)) || null}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(event, newValue) => {
                      handleInputChange("ciacodigo", newValue ? newValue.id : "")
                      handleInputChange("clicodigo", "")
                      setCriterioBusqueda("")
                      setNombreCliente("")
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Compañía" size="small" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="RUC o Código de Cliente"
                    size="small"
                    placeholder="Escriba y presione Enter..."
                    value={criterioBusqueda}
                    onChange={(e) => setCriterioBusqueda(e.target.value.toUpperCase())}
                    onKeyDown={handleKeyDownBusqueda}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleBuscarCliente} color="primary" size="small">
                            <PersonSearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      bgcolor: "#e1f5fe",
                      borderRadius: 1,
                      minHeight: "40px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={nombreCliente ? "primary.dark" : "text.secondary"}
                    >
                      {nombreCliente || "Cliente no seleccionado"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Inicial"
                    value={filtros.fecEmiDesde}
                    setValue={(v) => handleInputChange("fecEmiDesde", v)}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Final"
                    value={filtros.fecEmiHasta}
                    setValue={(v) => handleInputChange("fecEmiHasta", v)}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filtros.chkDG === "S"}
                        onChange={(e) => handleInputChange("chkDG", e.target.checked ? "S" : "N")}
                        color="secondary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold" color="secondary">
                        (-) Documentos en Garantía
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ fontWeight: "bold" }}
                  >
                    Consultar
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA ESTADO DE CUENTA */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            endpoint={refreshTableKey === 0 ? "" : "/ConsEstadodeCuenta/getEstadoCuentaCliente"}
            endpointJson={{
              filters: {},
              externalFilters: {
                ...filtros,
                fecEmiDesde: filtros.fecEmiDesde ? dayjs(filtros.fecEmiDesde).format("DD-MM-YYYY") : "",
                fecEmiHasta: filtros.fecEmiHasta ? dayjs(filtros.fecEmiHasta).format("DD-MM-YYYY") : "",
              },
            }}
            errorMsgFilterSearch="Error al cargar el estado de cuenta."
            queryKeyModal="ConsEstadoCuentaData"
            perPage={100}
            rowActions={() => []}
            rowActionsWidthTable={0}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: row.original.es_separador
                  ? "#e1f5fe"
                  : row.original.es_resumen
                    ? "#fffde7"
                    : "inherit",
                borderBottom: row.original.es_separador ? "2px solid #196C87" : "none",
              },
            })}
            topToolbarCustomActions={({ table, device }) => {
              const acciones = selectedMenuInfo?.data?.barraAcciones || []
              const toolbarActions = []
              const exportarAction = acciones.find((a) => a.acccaption === "EXPORTAR")
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "pdf",
                      onClick: generarPDFBancario,
                    },
                    {
                      label: "Exportar Excel/CSV",
                      key: "csv",
                      onClick: handleExportarEstadoCuentaCSV,
                    },
                  ],
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "fecha",
                header: "Emisión",
                size: 100,
                Cell: ({ cell, row }) => (
                  <span style={{ fontWeight: row.original.es_cabecera ? "bold" : "normal" }}>{cell.getValue()}</span>
                ),
              },
              { accessorKey: "vencimiento", header: "Vence", size: 100 },
              {
                accessorKey: "documento",
                header: "Documento / Detalle",
                size: 250,
                Cell: ({ cell, row }) => (
                  <span
                    style={{
                      fontWeight: row.original.es_cabecera ? "bold" : "normal",
                      color: row.original.es_separador ? "#196C87" : "inherit",
                      fontSize: row.original.es_separador ? "1rem" : "inherit",
                      paddingLeft: row.original.es_cabecera ? "0px" : "20px",
                    }}
                  >
                    {cell.getValue()}
                  </span>
                ),
              },
              { accessorKey: "tipo", header: "Tipo", size: 160 },
              {
                accessorKey: "valor",
                header: "Valor",
                size: 110,
                Cell: ({ cell, row }) => {
                  if (row.original.es_separador) return ""
                  const val = Number(cell.getValue() || 0)
                  const isDG = row.original.tipo_cod === "DG" || row.original.observacion?.includes("Garantía")
                  const color = val < 0 ? "#d32f2f" : isDG ? "#2e7d32" : "inherit"
                  return (
                    <span style={{ fontWeight: row.original.es_cabecera ? "bold" : "normal", color }}>
                      {val !== 0 ? `$${val.toFixed(2)}` : "0.00"}
                    </span>
                  )
                },
              },
              {
                accessorKey: "saldo",
                header: "Saldo",
                size: 110,
                Cell: ({ cell, row }) => {
                  if (row.original.es_separador || cell.getValue() === null) return ""
                  const val = Number(cell.getValue() || 0)
                  return (
                    <span
                      style={{ fontWeight: "bold", color: val < 0 ? "#d32f2f" : "inherit" }}
                    >{`$${val.toFixed(2)}`}</span>
                  )
                },
              },
              { accessorKey: "referencia", header: "Referencia", size: 220 },
              { accessorKey: "observacion", header: "Observación", size: 280 },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ConsEstadodeCuentaIndex
